/**
 * NOJU PDP — height-based size finder in size guide modal.
 * Baggy fit: height ranges overlap, so several sizes can work per height.
 */

/** @type {Record<string, { primary: string, also: string[], fit: string[] }>} */
const HEIGHT_BANDS = {
  'under-170': { primary: 'S', also: [], fit: ['S'] },
  '170-174': { primary: 'M', also: ['S'], fit: ['S', 'M'] },
  '175-178': { primary: 'L', also: ['M'], fit: ['M', 'L'] },
  '179-184': { primary: 'L', also: [], fit: ['L'] },
  '185-plus': { primary: 'XL', also: ['L'], fit: ['L', 'XL'] },
};

/**
 * @param {string} size
 * @returns {HTMLInputElement | null}
 */
function findSizeRadio(size) {
  const picker = document.querySelector('variant-picker[data-template-product-match="true"]');
  if (!picker) return null;

  const radios = picker.querySelectorAll('input[type="radio"]');
  for (const radio of radios) {
    if (!(radio instanceof HTMLInputElement)) continue;
    if (radio.value.trim().toUpperCase() === size.toUpperCase()) return radio;
  }

  return null;
}

/**
 * @param {string} size
 * @returns {boolean}
 */
function isSizeAvailable(size) {
  const radio = findSizeRadio(size);
  return (
    !!radio &&
    radio.dataset.optionAvailable !== 'false' &&
    radio.getAttribute('aria-disabled') !== 'true'
  );
}

/**
 * @param {string} size
 * @returns {boolean}
 */
function selectProductSize(size) {
  const radio = findSizeRadio(size);
  if (!radio) return false;

  if (!isSizeAvailable(size)) return false;

  radio.checked = true;
  radio.dispatchEvent(new Event('change', { bubbles: true }));
  return true;
}

/**
 * @param {HTMLElement} finder
 */
function initSizeFinder(finder) {
  const bandButtons = finder.querySelectorAll('[data-noju-size-finder-band]');
  const spectrumSegments = finder.querySelectorAll('[data-noju-size-finder-segment]');
  const resultEl = finder.querySelector('[data-noju-size-finder-result]');
  const resultTextEl = finder.querySelector('[data-noju-size-finder-result-text]');
  const resultAlsoEl = finder.querySelector('[data-noju-size-finder-result-also]');
  const selectBtn = finder.querySelector('[data-noju-size-finder-select]');
  const unavailableEl = finder.querySelector('[data-noju-size-finder-unavailable]');

  if (!resultEl || !resultTextEl || !selectBtn) return;

  const resultTemplate = finder.dataset.resultText || 'We recommend size {{ size }}';
  const resultAlsoTemplate = finder.dataset.resultAlsoText || 'Also works: {{ sizes }}';
  const selectTemplate = finder.dataset.selectText || 'Select size {{ size }}';
  const unavailableTemplate = finder.dataset.unavailableText || 'Size {{ size }} is currently unavailable';

  /** @type {string | null} */
  let recommendedSize = null;

  /**
   * @param {string} template
   * @param {string} key
   * @param {string} value
   */
  const formatKey = (template, key, value) => template.replace(new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g'), value);

  /**
   * @param {string} primary
   * @param {string[]} fit
   */
  const setActiveBand = (primary, fit) => {
    spectrumSegments.forEach((segment) => {
      const segmentSize = segment.getAttribute('data-noju-size-finder-segment');
      const isPrimary = segmentSize === primary;
      const isFit = !!segmentSize && fit.includes(segmentSize);

      segment.classList.toggle('is-active', isPrimary);
      segment.classList.toggle('is-fit', isFit && !isPrimary);
    });
  };

  bandButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const band = button.getAttribute('data-noju-size-finder-band');
      if (!band || !HEIGHT_BANDS[band]) return;

      const { primary, also, fit } = HEIGHT_BANDS[band];
      recommendedSize = primary;

      bandButtons.forEach((btn) => {
        const isActive = btn === button;
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      setActiveBand(primary, fit);

      resultTextEl.textContent = formatKey(resultTemplate, 'size', primary);
      selectBtn.textContent = formatKey(selectTemplate, 'size', primary);

      if (resultAlsoEl) {
        if (also.length > 0) {
          resultAlsoEl.textContent = formatKey(resultAlsoTemplate, 'sizes', also.join(', '));
          resultAlsoEl.hidden = false;
        } else {
          resultAlsoEl.textContent = '';
          resultAlsoEl.hidden = true;
        }
      }

      const isAvailable = isSizeAvailable(primary);
      selectBtn.hidden = !isAvailable;
      if (unavailableEl) {
        unavailableEl.hidden = isAvailable;
        if (!isAvailable) unavailableEl.textContent = formatKey(unavailableTemplate, 'size', primary);
      }

      resultEl.hidden = false;
    });
  });

  selectBtn.addEventListener('click', () => {
    if (!recommendedSize) return;
    if (!selectProductSize(recommendedSize)) return;

    const modal = document.getElementById('noju-size-guide-modal');
    if (modal && 'closeDialog' in modal && typeof modal.closeDialog === 'function') {
      modal.closeDialog();
    }
  });
}

function init() {
  document.querySelectorAll('[data-noju-size-finder]').forEach((finder) => {
    if (finder instanceof HTMLElement) initSizeFinder(finder);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}